import { Match, Option } from 'effect';
import { isFunction } from 'effect/Function';
import { multiply, parse } from 'effect/Number';
import {
  baseExceptionHandling,
  exception1,
  exception10_11,
  exception14,
  exception2,
  exception3,
  exception4,
  exception5,
  exception6,
  exception7,
  exception8,
} from './exceptions';
import {
  CheckFnParameters,
  ModulusCheckOverrides,
  SortCodeAccount,
  SortCodeWeightTable,
  WeightTableItem,
  getAccount,
  hasException,
} from './modulus-model';

const parseException = Option.flatMap(parse);

/**
 * ## Sort Code Validity Checker
 *
 * Performs Modulus checks as specified in [UK Modulus Checking](https://www.vocalink.com/media/a2febq5m/validating-account-numbers-uk-modulus-checking-v7-90.pdf)
 *
 *
 * @param sortCode string consisting of 6 digits
 * @param accountNumber string consisting of 8 digits
 *
 * @param weightTable
 *
 * @returns {boolean} `true` if the sortcode + account number are valid
 * `false` if the sortcode + account number are not valid
 */
export function checkSortCodeValidity(
  weightTable: SortCodeWeightTable,
): (account: SortCodeAccount) => boolean {
  return (account: SortCodeAccount) => {
    let { sortCode, accountNumber } = account;

    const checks = getCheckFromWeightTable(sortCode, weightTable);

    const results = checks
      .map((item) => addExceptionHandling(item))
      .map((check, checkNumber) => {
        // get function according to algorithm
        const checkFn = registerModulusCheck(checks[checkNumber]);

        // execute substitutions
        accountNumber = check.applyAccountNumberSubstitutions({
          accountNumber,
          checkNumber,
        });
        sortCode = check.applySortCodeSubstitutions({ sortCode, checkNumber });

        const weight = check.replaceWeight({
          account: getAccount(accountNumber, sortCode),
          weight: checks[checkNumber].weight,
          checkNumber,
          weightTable,
        });

        // perform check function
        return checkFn({
          account: getAccount(accountNumber, sortCode),
          weight: Object.values(weight),
          check,
        });
      });

    // For Exceptions that should pass with one of the selected checks
    if (
      checks.length < 2 ||
      (!hasException(checks[0]) && !hasException(checks[1]))
    ) {
      return results.every((result) => !!result);
    }
    const check1Exception = Option.getOrNull(
      parseException(checks[0].exception),
    );
    const check2Exception = Option.getOrNull(
      parseException(checks[1].exception),
    );

    if (check1Exception && check2Exception) {
      if (
        (check1Exception === 2 && check2Exception === 9) ||
        (check1Exception === 10 && check2Exception === 11) ||
        (check1Exception === 12 && check2Exception === 13) ||
        (check1Exception === 14 && check2Exception === 14)
      ) {
        return results.some((result) => !!result);
      }
    }

    // If nothing else specified, all checks need to pass for the
    // result to be valid.
    return results.every((result) => !!result);
  };
}

/**
 * Return function for evaluation for each algorithm
 *
 * @param check
 * @returns
 */
function registerModulusCheck(
  check: WeightTableItem,
): (opts: CheckFnParameters) => boolean {
  return Match.value(check.algorithm).pipe(
    Match.when('DBLAL', () => dblAlCheck),
    Match.when('MOD10', () => mod10Check),
    Match.when('MOD11', () => mod11Check),
    Match.exhaustive,
  );
}

function addExceptionHandling(check: WeightTableItem): ModulusCheckOverrides {
  if (!check.exception) {
    return baseExceptionHandling;
  }

  const exception = parseException(check.exception);
  return Option.match(exception, {
    onSome: (value) => {
      switch (value) {
        case 1:
          return exception1;
        case 2:
        case 9:
          return exception2;
        case 3:
          return exception3;
        case 4:
          return exception4;
        case 5:
          return exception5;
        case 6:
          return exception6;
        case 7:
          return exception7;
        case 8:
          return exception8;
        case 10:
          return exception10_11;
        case 14:
          return exception14;
        default:
          return baseExceptionHandling;
      }
    },
    onNone: () => baseExceptionHandling,
  });
}

/**
 * DBLAL
 */
function dblAlCheck(opts: CheckFnParameters): boolean {
  const { account, weight, check: check } = opts;
  let weightedAccount: number[] = [];

  for (let i = 0; i < 14; i++) {
    weightedAccount[i] = multiply(account[i], weight[i]);
  }

  // For DBLAL check, the individual digits are used for the sum
  weightedAccount = weightedAccount
    .join('')
    .split('')
    .map((a) => Number(a));

  const sum = weightedAccount.reduce((a, b) => a + b, 0);

  if (isFunction(check?.overrideTest)) {
    return check.overrideTest({ sum, account, algorithm: 'DBLAL' });
  }

  return sum % 10 === 0;
}

/**
 * Mod-10
 */
function mod10Check(opts: CheckFnParameters): boolean {
  const { account, weight } = opts;
  const weightedAccount: number[] = [];

  for (let i = 0; i < 14; i++) {
    weightedAccount[i] = multiply(account[i], weight[i]);
  }

  const sum = weightedAccount.reduce((a, b) => a + b, 0);
  return sum % 10 === 0;
}

/**
 * Mod-11
 */
function mod11Check(opts: CheckFnParameters): boolean {
  const { account, weight, check } = opts;
  const weightedAccount: number[] = [];

  for (let i = 0; i < 14; i++) {
    weightedAccount[i] = multiply(account[i], weight[i]);
  }

  const sum = weightedAccount.reduce((a, b) => a + b, 0);
  if (isFunction(check?.overrideTest)) {
    return check.overrideTest({ sum, account, algorithm: 'MOD11' });
  }
  return sum % 11 === 0;
}

/**
 * Get Checks from weighttable
 * @returns
 */
export function getCheckFromWeightTable(
  sortCodeStr: string,
  weightTable: SortCodeWeightTable,
): ReadonlyArray<WeightTableItem> {
  const sortCode = Option.getOrThrow(parse(sortCodeStr));

  const checks = weightTable.filter((check) => {
    const start = Option.getOrThrow(parse(check.sortCodeRange.start));
    const end = Option.getOrThrow(parse(check.sortCodeRange.end));

    return sortCode >= start && sortCode <= end;
  });

  // perform the check for exception 14 twice
  if (checks[0] && hasException(checks[0])) {
    const exception = parseException(checks[0].exception);

    return Option.match(exception, {
      onSome: (value) => {
        if (value === 14) {
          return [checks[0], checks[0]];
        }
        return checks;
      },
      onNone: () => checks,
    });
  }

  return checks;
}
